import { LinearGradient } from 'expo-linear-gradient';
import React, { useEffect, useRef, useState } from 'react';
import {
  Animated,
  Easing,
  DimensionValue,
  StyleProp,
  ViewStyle,
} from 'react-native';

import { Box } from '@components/Restyle';
import theme, { Theme } from '@themes/theme';

/**
 * Generic shimmer placeholder (TA-10068). Compose several instances to
 * build a concrete layout — see `stories/atoms/Skeleton.stories.tsx` for
 * the project-card/list-row/task-row/activity-row reference patterns.
 *
 * `row`/`card` are just wider/taller default rectangles for simple
 * single-block placeholders; for anything with an icon, avatar, or
 * multiple text lines, compose `rect`/`text`/`circle` instances instead
 * (there is no multi-slot "smart" variant on purpose — see CLAUDE.md
 * rule 1, simplicity over abstraction).
 *
 * Deliberately NOT handled here — this is per-screen orchestration for
 * whichever screen adopts this component (see TA-10089):
 * - Minimum 150ms display time, even if the query resolves instantly.
 * - 100ms opacity fade-in when swapping the skeleton for real content.
 * - Repeating this 3x for a list/table region.
 * - Showing skeletons during a search debounce window instead of the
 *   real list.
 * - Never rendering an empty state while the query is still in flight.
 */
export type SkeletonVariant = 'rect' | 'text' | 'circle' | 'row' | 'card';

export interface SkeletonProps {
  variant?: SkeletonVariant;
  width?: DimensionValue;
  height?: DimensionValue;
  borderRadius?: keyof Theme['borderRadii'];
  style?: StyleProp<ViewStyle>;
  testID?: string;
}

const SHIMMER_DURATION_MS = 1400;

const VARIANT_DEFAULTS: Record<
  SkeletonVariant,
  {
    width: DimensionValue;
    height: DimensionValue;
    borderRadius: keyof Theme['borderRadii'];
  }
> = {
  rect: { width: '100%', height: 16, borderRadius: 'xs' },
  text: { width: '100%', height: 12, borderRadius: 'xxs' },
  circle: { width: 32, height: 32, borderRadius: 'xxl' },
  row: { width: '100%', height: 64, borderRadius: 's' },
  card: { width: '100%', height: 140, borderRadius: 'm' },
};

export const Skeleton: React.FC<SkeletonProps> = ({
  variant = 'rect',
  width,
  height,
  borderRadius,
  style,
  testID = 'skeleton',
}) => {
  const defaults = VARIANT_DEFAULTS[variant];
  const resolvedWidth = width ?? defaults.width;
  const resolvedHeight = height ?? defaults.height;
  const resolvedBorderRadius = borderRadius ?? defaults.borderRadius;

  const [containerWidth, setContainerWidth] = useState(0);
  const shimmerValue = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    const animation = Animated.loop(
      Animated.timing(shimmerValue, {
        toValue: 1,
        duration: SHIMMER_DURATION_MS,
        easing: Easing.inOut(Easing.ease),
        useNativeDriver: true,
      })
    );
    animation.start();

    return () => {
      animation.stop();
    };
  }, [shimmerValue]);

  const bandWidth = Math.max(containerWidth * 0.6, 60);
  const translateX = shimmerValue.interpolate({
    inputRange: [0, 1],
    outputRange: [-bandWidth, containerWidth + bandWidth],
  });

  const circleBorderRadius =
    variant === 'circle' &&
    typeof resolvedWidth === 'number' &&
    typeof resolvedHeight === 'number'
      ? Math.min(resolvedWidth, resolvedHeight) / 2
      : undefined;

  return (
    <Box
      testID={testID}
      backgroundColor='grey03'
      width={resolvedWidth}
      height={resolvedHeight}
      borderRadius={
        circleBorderRadius === undefined ? resolvedBorderRadius : undefined
      }
      overflow='hidden'
      style={[
        circleBorderRadius !== undefined && {
          borderRadius: circleBorderRadius,
        },
        style,
      ]}
      onLayout={(event) => setContainerWidth(event.nativeEvent.layout.width)}>
      <Animated.View
        pointerEvents='none'
        style={{
          position: 'absolute',
          top: 0,
          bottom: 0,
          width: bandWidth,
          transform: [{ translateX }],
        }}>
        <LinearGradient
          colors={['transparent', theme.colors.grey01, 'transparent']}
          start={{ x: 0, y: 0 }}
          end={{ x: 1, y: 0 }}
          style={{ flex: 1 }}
        />
      </Animated.View>
    </Box>
  );
};
